import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import UnitPageShell from '../components/shared/UnitPageShell';
import { useAuth } from '../context/AuthContext';
import { apiConfig } from '../utils/apiConfig';

type UpdateItem = {
  id: number;
  title: string;
  body: string;
  is_active: number | boolean;
  created_at?: string;
};

const AdminUpdates: React.FC = () => {
  const { token, canManageUpdates } = useAuth();
  const [updates, setUpdates] = useState<UpdateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newActive, setNewActive] = useState(true);

  const loadUpdates = useCallback(async () => {
    if (!token || !canManageUpdates) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiConfig.apiUrl}?action=getUpdates&includeInactive=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && Array.isArray(data.updates)) {
        setUpdates(data.updates);
      } else {
        setMessage({ severity: 'error', text: data?.error || 'Updates konnten nicht geladen werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Updates konnten nicht geladen werden.' });
    } finally {
      setLoading(false);
    }
  }, [token, canManageUpdates]);

  useEffect(() => {
    loadUpdates();
  }, [loadUpdates]);

  const handleCreate = async () => {
    if (!token) return;
    const title = newTitle.trim();
    const body = newBody.trim();
    if (!title || !body) {
      setMessage({ severity: 'info', text: 'Titel und Text sind erforderlich.' });
      return;
    }
    setSavingId(-1);
    setMessage(null);
    try {
      const res = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'createUpdate',
          title,
          body,
          isActive: newActive
        })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        setNewTitle('');
        setNewBody('');
        setNewActive(true);
        await loadUpdates();
        setMessage({ severity: 'success', text: 'Update wurde erstellt.' });
      } else {
        setMessage({ severity: 'error', text: data?.error || 'Update konnte nicht erstellt werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Update konnte nicht erstellt werden.' });
    } finally {
      setSavingId(null);
    }
  };

  const handleUpdate = async (item: UpdateItem) => {
    if (!token) return;
    setSavingId(item.id);
    setMessage(null);
    try {
      const res = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateUpdate',
          id: item.id,
          title: item.title,
          body: item.body,
          isActive: item.is_active
        })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        setMessage({ severity: 'success', text: 'Update wurde gespeichert.' });
        await loadUpdates();
      } else {
        setMessage({ severity: 'error', text: data?.error || 'Update konnte nicht gespeichert werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Update konnte nicht gespeichert werden.' });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    if (!window.confirm('Update wirklich loeschen?')) return;
    setSavingId(id);
    setMessage(null);
    try {
      const res = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'deleteUpdate', id })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await loadUpdates();
        setMessage({ severity: 'success', text: 'Update wurde geloescht.' });
      } else {
        setMessage({ severity: 'error', text: data?.error || 'Update konnte nicht geloescht werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Update konnte nicht geloescht werden.' });
    } finally {
      setSavingId(null);
    }
  };

  if (!token || !canManageUpdates) {
    return (
      <UnitPageShell title="Updates">
        <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Container>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="Admin: Updates verwalten" showTTS={false}>
      <Container maxWidth={false} disableGutters sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: { xs: 2, md: 4 } }} elevation={1}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Updates verwalten
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bitte in einfacher Sprache schreiben, damit alle Nutzer den Inhalt verstehen.
              </Typography>
            </Box>

            {message && <Alert severity={message.severity}>{message.text}</Alert>}

            <Box sx={{ border: '1px solid #eee', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Neues Update
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Titel"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Text"
                  value={newBody}
                  onChange={(e) => setNewBody(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Switch
                    checked={newActive}
                    onChange={(e) => setNewActive(e.target.checked)}
                  />
                  <Typography variant="body2">Aktiv im Banner</Typography>
                </Stack>
                <Button
                  variant="contained"
                  onClick={handleCreate}
                  disabled={savingId === -1}
                >
                  {savingId === -1 ? 'Speichere ...' : 'Update anlegen'}
                </Button>
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Bestehende Updates
              </Typography>
              {loading ? (
                <Typography variant="body2">Lade Updates ...</Typography>
              ) : updates.length === 0 ? (
                <Typography variant="body2">Keine Updates vorhanden.</Typography>
              ) : (
                <Stack spacing={2}>
                  {updates.map((item) => (
                    <Paper key={item.id} sx={{ p: 2 }} variant="outlined">
                      <Stack spacing={2}>
                        <TextField
                          label="Titel"
                          value={item.title}
                          onChange={(e) =>
                            setUpdates((prev) =>
                              prev.map((u) => (u.id === item.id ? { ...u, title: e.target.value } : u))
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Text"
                          value={item.body}
                          onChange={(e) =>
                            setUpdates((prev) =>
                              prev.map((u) => (u.id === item.id ? { ...u, body: e.target.value } : u))
                            )
                          }
                          fullWidth
                          multiline
                          minRows={3}
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Switch
                              checked={Boolean(item.is_active)}
                              onChange={(e) =>
                                setUpdates((prev) =>
                                  prev.map((u) => (u.id === item.id ? { ...u, is_active: e.target.checked } : u))
                                )
                              }
                            />
                            <Typography variant="body2">Aktiv im Banner</Typography>
                          </Stack>
                          <Box sx={{ flexGrow: 1 }} />
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              onClick={() => handleUpdate(item)}
                              disabled={savingId === item.id}
                            >
                              {savingId === item.id ? 'Speichere ...' : 'Speichern'}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleDelete(item.id)}
                              disabled={savingId === item.id}
                            >
                              Loeschen
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Paper>
      </Container>
    </UnitPageShell>
  );
};

export default AdminUpdates;
